/*
  This component renders a book with a responsive layout.

  It includes primary content (title, author, CommunityRating),
  optional secondary content (expandable description),
  and optional actions content (WantToReadButton).

  Wrapper components should be created to specify options for
  the ResponsiveBook in different contexts (see SeriesBook).

  Ref: https://w.amazon.com/bin/view/Goodreads/Teams/Mobile_web/Responsive_SeriesList_Components_Refactor/
*/

import ExpandableHtml from "./user_content/expandable_html";
import CommunityRating from "./community_rating";
import AuthorBadge from "./author/author_badge";
import WantToReadButton, {
  ALWAYS_DISPLAY_STARS_PROP,
} from "./want_to_read/want_to_read_button";
import GrPropTypes from "./shared/gr_prop_types";
import { PropTypes } from "react";

const ResponsiveBook = ({
  book,
  communityRatingOptions,
  displayableTitle,
  includeDescription,
  includeWTRButton,
  ...props
}) => {
  const title = displayableTitle ? displayableTitle : book.title;

  const authorLink = book.author ? (
    <span itemProp="name">
      <a
        itemProp="url"
        href={book.author.profileUrl}
        className="gr-hyperlink gr-hyperlink--naked">
        {book.author.name}
      </a>
      <AuthorBadge isGoodreadsAuthor={book.author.isGoodreadsAuthor} />
    </span>
  ) : (
    "deleted author"
  );

  const ratingsCount = communityRatingOptions.displayRatingsCount
    ? book.ratingsCount
    : null;
  const textReviewsCount = communityRatingOptions.displayTextReviewsCount
    ? book.textReviewsCount
    : null;
  const publication = communityRatingOptions.displayPublicationDate
    ? { toBePublished: book.toBePublished, date: book.publicationDate }
    : null;
  const editions = communityRatingOptions.displayEditionsCount
    ? { countString: book.editions, url: book.editionsUrl }
    : null;

  const primary = (
    <div className="u-paddingBottomXSmall">
      <a
        className="gr-h3 gr-h3--serif gr-h3--noMargin"
        href={book.bookUrl}
        itemProp="url">
        <span itemProp="name">{title}</span>
      </a>

      <div className="u-paddingBottomTiny">
        {"by "}
        <span itemProp="author" itemScope itemType="http://schema.org/Person">
          {authorLink}
        </span>
      </div>

      {communityRatingOptions.displayRating && (
        <CommunityRating
          rating={parseFloat(book.avgRating)}
          size={CommunityRating.ICON_SIZES.MEDIUM}
          displayAvg={true}
          displayAvgRatingText={communityRatingOptions.displayAvgRatingText}
          ratingsCount={ratingsCount}
          textReviewsCount={textReviewsCount}
          publication={publication}
          editions={editions}
        />
      )}
    </div>
  );

  const secondary = (
    <div>
      <ExpandableHtml {...book.description} />
    </div>
  );

  const actions = (
    <WantToReadButton
      bookTitle={title}
      uniqueId={Math.random(1000000).toString()}
      bookId={book.bookId}
      withModalShelvingMenu={true}
      showStars={ALWAYS_DISPLAY_STARS_PROP}
    />
  );

  return (
    <div className="responsiveBook" itemScope itemType="http://schema.org/Book">
      <div className="objectLockupContent">
        <div className="objectLockupContent__media">
          <div className="responsiveBook__media">
            <a href={book.bookUrl}>
              <img
                src={book.imageUrl}
                alt={title}
                className="gr-box--withShadow responsiveBook__img"
                itemProp="image"
              />
            </a>
          </div>
        </div>
        {primary}
        <div className="objectLockupContent__secondary">
          {includeDescription && secondary}
          {props.children}
        </div>
        {includeWTRButton && (
          <div className="objectLockupContent__actions u-paddingTopXSmall">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

ResponsiveBook.propTypes = {
  book: GrPropTypes.book({
    avgRating: PropTypes.number,
  }).isRequired,
  communityRatingOptions: PropTypes.shape({
    displayRating: PropTypes.bool,
    displayAvgRatingText: PropTypes.bool,
    displayRatingsCount: PropTypes.bool,
    displayTextReviewsCount: PropTypes.bool,
    displayPublicationDate: PropTypes.bool,
    displayEditionsCount: PropTypes.bool,
  }).isRequired,
  displayableTitle: PropTypes.string,
  includeDescription: PropTypes.bool,
  includeWTRButton: PropTypes.bool,
};

export default ResponsiveBook;
